import { useCallback, useEffect, useState } from 'react';
import { apiClient, endpoints } from '../api/client';

export interface TaskGroup {
  id: string;
  name: string;
  brand_name?: string | null;
  product_name?: string | null;
  task_ids: string[];
  created_at: string;
}

export interface TaskGroupCreate {
  name: string;
  brand_name?: string | null;
  product_name?: string | null;
  task_ids: string[];
}

export interface TaskGroupUpdate {
  name?: string;
  brand_name?: string | null;
  product_name?: string | null;
  task_ids?: string[];
}

export interface SuggestedKeyword {
  text: string;
  source: string;
  kind: 'product_line' | 'bait' | string;
  product_name?: string | null;
}

export interface SuggestedMarketplace {
  definition_id: string;
  name?: string;
  domain?: string;
  last_success_at?: string | null;
  execution_mode?: string;
  max_pages?: number | null;
  max_stage2_products?: number | null;
  scrape_health?: string;
  selected?: boolean;
  brand_match?: boolean;
}

export interface TaskGroupSuggestResponse {
  keywords: SuggestedKeyword[];
  marketplaces: SuggestedMarketplace[];
  suggested_name: string;
  ai_error?: string;
}

export const useTaskGroups = () => {
  const [groups, setGroups] = useState<TaskGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(endpoints.taskGroups);
      setGroups(res.data.items || []);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Failed to fetch task groups');
    } finally {
      setLoading(false);
    }
  }, []);

  const createGroup = useCallback(
    async (payload: TaskGroupCreate): Promise<TaskGroup | null> => {
      try {
        const res = await apiClient.post(endpoints.taskGroups, payload);
        await fetchGroups();
        return res.data as TaskGroup;
      } catch (e) {
        setError('Failed to create task group');
        return null;
      }
    },
    [fetchGroups]
  );

  const updateGroup = useCallback(
    async (id: string, payload: TaskGroupUpdate): Promise<boolean> => {
      try {
        await apiClient.put(endpoints.taskGroup(id), payload);
        await fetchGroups();
        return true;
      } catch (e) {
        setError('Failed to update task group');
        return false;
      }
    },
    [fetchGroups]
  );

  const deleteGroup = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await apiClient.delete(endpoints.taskGroup(id));
        await fetchGroups();
        return true;
      } catch (e) {
        setError('Failed to delete task group');
        return false;
      }
    },
    [fetchGroups]
  );

  const suggestGroup = useCallback(
    async (
      brandName: string,
      products: Array<{
        name: string;
        keywords?: string[];
        category?: string;
        identifier?: string;
      }>
    ): Promise<TaskGroupSuggestResponse | null> => {
      try {
        const res = await apiClient.post(endpoints.taskGroupSuggest, {
          brand_name: brandName,
          products,
        });
        return res.data as TaskGroupSuggestResponse;
      } catch (e: any) {
        setError(e?.response?.data?.detail || e.message || 'Failed to suggest group');
        return null;
      }
    },
    []
  );

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return {
    groups,
    loading,
    error,
    fetchGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    suggestGroup,
  };
};
